import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import SearchFilter from "@/Components/common/client/SubBranches/SearchFilter";
import ModalPopUp from "@/Components/common/ModalPopUp";
import ReadMoreWrapper from "@/Components/common/ReadMoreWrapper";
import SubBranchesLoader from "@/Components/common/SubBranchesLoader";
import TableNoContent from "@/Components/common/TableNoContent";
import { CalendarArrow } from "@/Components/Icons/CalendarArrow";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import { routingConstants } from "@/Constants/routingConstants";
import { saveState } from "@/localStorage";
import {
  toggleClientBranchesModal,
  updateFilters,
  fetchClientBranches,
} from "@/reducers/clientcommon";
import { setSession } from "@/reducers/session";
import { _impersonateUser } from "@/services/auth";
import store from "@/store";
import { showErrorToast } from "@/utilities/utils";

import { toastMessageText } from "../../../utilities/CommonText";

const SubBranchesModal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { showClientBranchesModal, clientBranches, isLoading } = useSelector(
    (state) => state.clientCommon,
  );
  const { session } = useSelector((state) => state.session);
  const { user } = useSelector((state) => state.clientProfile);

  const [activeBranch, setActiveBranch] = useState({});
  const [expandedBranchId, setExpandedBranchId] = useState(null);
  const [disabledBranch, setDisabledBranch] = useState(false);

  useEffect(() => {
    if (showClientBranchesModal) {
      dispatch(fetchClientBranches({}));
    }
  }, [showClientBranchesModal]);

  useEffect(() => {
    if (clientBranches.length) {
      const activeBranch = clientBranches.find(
        (branch) => branch.client_branch_id === user.client_id,
      );
      setActiveBranch(activeBranch);
    }
  }, [clientBranches, user.client_id]);

  const toggleBranchDetails = (id) => {
    setExpandedBranchId((prevId) => (prevId === id ? null : id));
  };

  const handleClose = () => {
    dispatch(updateFilters({ search: "" }));
    dispatch(toggleClientBranchesModal(false));
  };

  const handleLogin = async (branch) => {
    const { client_branch_id } = branch;
    if (disabledBranch || activeBranch?.client_branch_id === client_branch_id) {
      showErrorToast(
        toastMessageText?.alreadyLoginInBranch(branch?.client_name),
        {
          id: "branch_error",
        },
      );
      return;
    }
    setDisabledBranch(true);
    try {
      const { data } = await _impersonateUser({ client_id: client_branch_id });
      if (data) {
        handleSessionUpdate(data);
        setTimeout(() => {
          window.location.reload();
        }, 200);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDisabledBranch(false);
    }
  };

  const handleSessionUpdate = (data) => {
    const { pathname } = location;
    const { user } = data;
    //  Update session data in redux store
    const clientData = {
      ...user,
      client_id: user?.client_id || "",
      firstname: user?.firstname || "",
      profile_image: user?.profile_image || "",
      visit_per: user?.visit_per || "",
      parking: user?.parking || "",
      food_drink: user?.food_drink || "",
      admin_per: user?.admin_per || "",
      vat_on_applocum_per: user?.vat_on_applocum_per || "",
      client_manager_name: user?.client_manager_name || "",
      client_practice_code: user?.client_practice_code || "",
      qr_code: user?.qr_code || "",
      qr_code_updated_at: user?.qr_code_updated_at || "",
      client_consultant_email: user?.client_consultant_email || "",
      client_address: user?.client_address || "",
      client_phone_number: user?.client_phone_number || "",
      client_ref_number: user?.client_ref_number || "",
      client_branch_name: user?.client_branch_name || "",
    };

    if (session.user_type === "Client") {
      // currentlty client_user has active not client. So, we are updating only client_user.backup condition here.
      dispatch(setSession(clientData));
    } else if (session.user_type === "Client_User") {
      const callback = () => {
        saveState(store.getState());
        dispatch(toggleClientBranchesModal(false));
        //  Redirect to dashboard
        const redirectPath =
          pathname === routingConstants.dashboard
            ? routingConstants.dashboardRedirect
            : routingConstants.dashboard;
        navigate(redirectPath, { state: { _for: routingConstants.dashboard } });
      };
      // Update session data in redux store
      dispatch(
        setSession({
          client_id: data.client_id,
          original_user: session.user_id,
          client_branch_name: data.client_branch_name || "",
          client_name: data.client_branch_name || "",
          client_practice_code: data.client_practice_code || "",
          qr_code_key: data.qr_code || "",
          qr_code_updated_at: data.qr_code_updated_at || "",
          login_as: data.login_as || "",
          is_bank_client: data.is_bank_client || "",
          callback,
        }),
      );
    }
  };

  return (
    <ModalPopUp
      show={showClientBranchesModal}
      panelClass="max-w-[800px] max-sm:max-w-[400px]"
      onClose={handleClose}
    >
      <div className="subBranchesModal_Wrapper">
        <div className="mb-5 flex flex-col gap-4">
          <div className="flex justify-between">
            <h3 className="text-[19px] font-semibold leading-[normal] text-black">
              Branches
            </h3>
            <div className="flex !items-center !justify-center gap-5">
              <p className="text-base font-medium leading-[normal] text-black">
                {clientBranches.length > 0 && (
                  <>
                    Total Branches: <b>{clientBranches.length}</b>
                  </>
                )}
              </p>
              <button
                onClick={handleClose}
                className="text-red outline-none ring-0"
              >
                <RejectIcon />
              </button>
            </div>
          </div>
        </div>
        <SearchFilter />
        <div className="SubBranchescustom-scrollbar-ui mt-5 h-[25.1rem] w-full overflow-y-auto">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <SubBranchesLoader key={index} />
            ))
          ) : clientBranches.length === 0 ? (
            <TableNoContent />
          ) : (
            <div className="_subBranchesCard mb-2 mr-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {clientBranches.map((branch) => {
                const {
                  client_branch_id,
                  client_name,
                  client_practice_code,
                  client_telno,
                  client_a1,
                } = branch;
                const isExpanded = expandedBranchId === client_branch_id;
                const isActive =
                  activeBranch?.client_branch_id === client_branch_id;
                return (
                  <div
                    key={client_branch_id}
                    className={`group h-fit rounded-lg border border-[#E9F1FD] transition-all duration-300 hover:shadow-sm ${isExpanded || isActive ? "bg-grey" : ""}`}
                  >
                    <div className="flex justify-between p-[14px_14px_0px_14px]">
                      <div>
                        <p className="text-Base line-clamp-1 font-semibold capitalize">
                          {client_name}
                        </p>
                        <p className="mt-1 text-[14px] font-semibold text-txtColor">
                          {client_practice_code || "-"}
                        </p>
                      </div>
                      <div>
                        <button
                          className={`select-none rounded-lg bg-lightblueBG p-[10px_14px] text-[14px] font-medium text-primary transition-all duration-300 group-hover:bg-blue group-hover:text-white ${isExpanded || isActive ? "!bg-blue text-white" : "text-blue"}`}
                          onClick={() => handleLogin(branch)}
                        >
                          Login
                        </button>
                      </div>
                    </div>

                    {isExpanded ? (
                      <>
                        <button
                          className="mb-[16px] mt-[16px] flex select-none items-center gap-2 px-[14px] text-[14px] font-semibold text-blue transition-all duration-300"
                          onClick={() => toggleBranchDetails(client_branch_id)}
                        >
                          Show less{" "}
                          <CalendarArrow cls="w-[11px] h-[11px] rotate-[270deg]" />
                        </button>
                        <div className="px-[14px] pb-[14px]">
                          <div className="flex gap-x-5">
                            <p className="text-[14px] font-semibold text-[#7E84AF]">
                              CONTACT:
                            </p>
                            <p className="text-[14px] font-semibold text-black">
                              {client_telno || "-"}
                            </p>
                          </div>
                          <div className="mt-2 flex gap-x-5">
                            <p className="text-[14px] font-semibold text-[#7E84AF]">
                              ADDRESS:
                            </p>
                            <ReadMoreWrapper
                              cls="pr-2 font-semibold text-[14px] text-black"
                              text={client_a1 || "-"}
                              readMoreText={
                                <span className="text-primary"> Read More</span>
                              }
                              readLessText={
                                <span className="text-primary"> Read Less</span>
                              }
                              max={97}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <button
                        className="mb-[16px] mt-[16px] flex select-none items-center gap-2 px-[14px] text-[14px] font-semibold text-blue transition-all duration-300"
                        onClick={() => toggleBranchDetails(client_branch_id)}
                      >
                        Show more{" "}
                        <CalendarArrow cls="w-[11px] h-[11px] -rotate-180" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalPopUp>
  );
};

export default SubBranchesModal;
